import { Anchor, List, Stack, Text } from '@mantine/core'

import { Section } from '../../components/SectionHeader'
import { LegalPage } from './LegalPage'

/**
 * `/privacy`: no sign-in needed. The source text is also kept in docs/legal/privacy.md for the owner to review in
 * git; keep the two in sync.
 */
export function PrivacyPage() {
  return (
    <LegalPage title="Privacy policy">
      <Text size="sm">
        Macrotide is a personal nutrition and training app from Ikaros Dev. This page explains what we collect, why,
        and what you can do about it. It&apos;s offered worldwide.
      </Text>
      <Text size="sm">
        <b>Controller:</b> Ikaros Dev. Contact: macrotide@ikarosdev.com.
      </Text>

      <Section title="Guest mode works without an account" card={false}>
        <Text size="sm">
          Everything in Macrotide except AI features works without an account. In guest mode, your data stays on
          your device and never reaches our servers. If you create an account, your phone uploads everything you
          already have so your devices match, and from then on your data syncs to our servers on AWS, in the
          eu-west-2 (London) region.
        </Text>
      </Section>

      <Section title="What we collect" card={false}>
        <Text size="sm">
          If you choose to enter it, Macrotide stores: your food log, recipes and custom foods, weight, body metrics
          including waist, your profile (sex, birth date, height, activity level, and optionally body fat % and
          ethnic background), goals, workouts, training programs, equipment, water, habits, and photos (progress
          photos and food photos you send to AI).
        </Text>
      </Section>

      <Section title="Health Connect (Android)" card={false}>
        <Stack gap="sm">
          <Text size="sm">
            With your permission, Macrotide reads weight, body fat, steps, active calories, sleep, heart rate
            variability, resting heart rate and exercise sessions from Health Connect (exercise sessions are read
            live and not stored separately). It writes weight, body fat, nutrition, water and workouts back to
            Health Connect so other apps stay in sync.
          </Text>
          <Text size="sm">
            Data read from Health Connect is saved in the app and, when you&apos;re signed in, synced to your own
            account so your devices match. We never sell this data, never use it for advertising, and never use it
            to train AI models.
          </Text>
          <Text size="sm">
            <b>Health Connect limited-use statement:</b> Macrotide&apos;s use of data from Health Connect is limited
            to providing the features described in this policy. We do not use Health Connect data for advertising
            or sell it to data brokers or information resellers, and any use for purposes unrelated to this
            app&apos;s core features is only ever with your consent.
          </Text>
        </Stack>
      </Section>

      <Section title="AI features" card={false}>
        <Stack gap="sm">
          <Text size="sm">
            When you ask Macrotide to read a photo or description, that content (and only that content) goes to
            OpenRouter, which routes it to AI model providers (for example Anthropic or OpenAI) to estimate
            nutrition or adjust your plans. We don&apos;t send your profile, body metrics or Health Connect data to
            AI — only what you explicitly submit to an AI feature.
          </Text>
          <Text size="sm">
            Our OpenRouter account is configured so providers don&apos;t use your data to train their models;
            we&apos;ve deliberately avoided models that would require turning that setting on.
          </Text>
        </Stack>
      </Section>

      <Section title="Open Food Facts" card={false}>
        <Text size="sm">
          When you scan a barcode, only the barcode number is sent, directly from your device, to Open Food
          Facts&apos; public API. No account or personal data travels with it.
        </Text>
      </Section>

      <Section title="Ethnic background" card={false}>
        <Text size="sm">
          Your ethnic background is optional, and under UK and EU data protection law it&apos;s &quot;special
          category data&quot;. We only ask for it, and only use it, to choose the right NICE BMI threshold for you.
          It&apos;s collected only with your explicit consent, it&apos;s never sent to AI, and you can clear it at
          any time in Settings.
        </Text>
      </Section>

      <Section title="Why we can use your data (lawful bases)" card={false}>
        <List size="sm">
          <List.Item>
            <b>Contract:</b> to provide the service you&apos;ve asked for.
          </List.Item>
          <List.Item>
            <b>Explicit consent:</b> for health data (Health Connect, body metrics) and ethnic background.
          </List.Item>
          <List.Item>
            <b>Legitimate interests:</b> for security and preventing abuse of the service.
          </List.Item>
        </List>
      </Section>

      <Section title="How long we keep it" card={false}>
        <Text size="sm">
          We keep your data until you delete it or your account. Deleting your account starts a{' '}
          <b>30-day grace period</b>: signing in again before it ends cancels the deletion and your account carries
          on as before. After the 30 days, a nightly job permanently deletes your account and everything it holds,
          and our backups roll off a further 30 days after that. AI reports (see &quot;Report this result&quot; in
          the app) are kept for 1 year.
        </Text>
      </Section>

      <Section title="International transfers" card={false}>
        <Text size="sm">
          Your data is stored on AWS in eu-west-2 (London). OpenRouter and the AI model providers it uses may
          process data in the US; where that happens, it&apos;s under standard contractual clauses or the UK&apos;s
          International Data Transfer Addendum.
        </Text>
      </Section>

      <Section title="Your rights" card={false}>
        <Text size="sm">
          You can ask to access, correct, delete, restrict or export (port) your data, object to how we use it, and
          withdraw consent at any time. The app can export everything you&apos;ve logged as a download (Settings ›
          Data &amp; integrations). You can also complain to the UK Information Commissioner&apos;s Office
          (ico.org.uk) or your local data protection authority.
        </Text>
        <Text size="sm" mt="xs">
          <b>EU representative:</b> [EU representative: to be appointed]
        </Text>
      </Section>

      <Section title="California residents (CCPA/CPRA)" card={false}>
        <Text size="sm">We don&apos;t sell or share your personal information.</Text>
      </Section>

      <Section title="Children" card={false}>
        <Text size="sm">
          Macrotide isn&apos;t intended for anyone under 18. Weight-loss goals are blocked for anyone under 18, and
          the app stores are set to an 18+ audience.
        </Text>
      </Section>

      <Section title="Security" card={false}>
        <Text size="sm">Data travels over HTTPS and is encrypted at rest.</Text>
      </Section>

      <Section title="Changes" card={false}>
        <Text size="sm">
          If we make a material change to this policy, we&apos;ll update the date at the top and tell you inside the
          app.
        </Text>
      </Section>

      <Section title="Deleting your account" card={false}>
        <Text size="sm">
          See <Anchor href="/delete-account">/delete-account</Anchor> for how.
        </Text>
      </Section>
    </LegalPage>
  )
}
